/**
 * Framework-free match/hand orchestration glue.
 *
 * Thin, purely-functional wrapper around `@joker-holdem/core`'s
 * match/hand APIs. Contains no timers, no Worker calls, no React/zustand
 * -- those live in `src/store/gameStore.ts`, which composes these
 * functions with async bot decisions and UI delays. Kept separate so the
 * "hand completes -> record exported -> match advances -> match-over
 * detected" logic can be unit tested without mocking a browser.
 */
import {
  applyAction,
  applyHandResultToMatch,
  createMatch,
  exportHandRecord,
  startNextHand,
  type Action,
  type HandConfig,
  type HandRecord,
  type HandState,
  type MatchState,
  type PlayerIndex,
  type Rng,
} from "@joker-holdem/core";

export interface BeginMatchResult {
  readonly match: MatchState;
  readonly hand: HandState;
}

/** Creates a fresh match and deals its first hand. */
export function beginMatch(
  config: HandConfig,
  rng: Rng,
  startingButton: PlayerIndex = 0
): BeginMatchResult {
  const match = createMatch(config, startingButton);
  const hand = startNextHand(match, rng);
  return { match, hand };
}

/** Deals the next hand using the match's current stacks/button. Throws if the match is already over. */
export function dealNextHand(match: MatchState, rng: Rng): HandState {
  return startNextHand(match, rng);
}

/** Applies a single legal action to the in-progress hand. */
export function applyGameAction(hand: HandState, action: Action): HandState {
  return applyAction(hand, action);
}

export interface HandCompletion {
  readonly record: HandRecord;
  readonly match: MatchState;
  readonly matchOver: boolean;
}

/**
 * Called once a hand's `result` is set. Exports the full HandRecord (for
 * persistence/history) and folds the result back into the match (stacks,
 * button rotation, hand counter, match-over detection). Throws if the
 * hand isn't actually finished yet.
 */
export function completeHand(match: MatchState, hand: HandState): HandCompletion {
  const record = exportHandRecord(hand);
  const nextMatch = applyHandResultToMatch(match, hand);
  return { record, match: nextMatch, matchOver: nextMatch.isOver };
}
